import { createSelector } from '@reduxjs/toolkit';

import { getMemberWorkspaceInfoList } from '../account';
import { selectAllVideoIds, selectVideoById } from '../dashboard';
import { getTemplateById } from '../templates';

import {
    type Blueprint,
    type DashboardVideo,
    type NarrativeThreadEnum,
    RoleEnum,
} from 'js/src/client/types.gen';
import type { PlainDropdownItem } from 'js/src/components/common/ui/Dropdown';
import { DROPDOWN_DIVIDER } from 'js/src/components/common/ui/Dropdown';
import { DROPDOWN_ACTIONS } from 'js/src/libs/blueprints-consts';
import type { DeepReadonly } from 'js/src/libs/consts';
import type { RootState } from 'js/src/libs/RootState';

export function getBlueprint(state: RootState, blueprintId: number | null): Blueprint | null {
    if (blueprintId === null) {
        return null;
    }
    const matchingBlueprint = state.blueprints.blueprintList.find(
        (blueprint) => blueprint.id === blueprintId,
    );
    if (matchingBlueprint) {
        return matchingBlueprint;
    }
    return null;
}

const EMPTY_BLUEPRINTS: Blueprint[] = [];

export function getCustomBlueprintsList(state: RootState): Blueprint[] {
    return state.blueprints?.blueprintList ?? EMPTY_BLUEPRINTS;
}

export const getBlueprintIds = createSelector(
    getCustomBlueprintsList,
    (blueprints): readonly number[] => blueprints.map((blueprint) => blueprint.id),
);

export const getBlueprintIdsByNarrativeThread = createSelector(
    getCustomBlueprintsList,
    (_: RootState, narrativeThread: NarrativeThreadEnum | null) => narrativeThread,
    (blueprints, narrativeThread): readonly number[] => {
        if (narrativeThread === null) {
            return blueprints.map((blueprint) => blueprint.id);
        }
        return blueprints
            .filter((blueprint) => blueprint.narrative_thread === narrativeThread)
            .map((blueprint) => blueprint.id);
    },
);

/**
 * Check if blueprints are currently loading
 */
export function isBlueprintsLoading(state: RootState): boolean {
    return state.blueprints.isLoading;
}

/**
 * Get the main video ID for a blueprint
 */
export function getBlueprintVideoId(state: RootState, blueprintId: number): number | null {
    const blueprint = getBlueprint(state, blueprintId);
    return blueprint?.video_ids?.[0] || null;
}

type BlueprintDropdownSelectData = { type: DROPDOWN_ACTIONS; workspaceId?: number };

/*
 * Create dropdown menu options for blueprint tiles
 */
export const selectDropdownItemProps = createSelector(
    (state: RootState) => getMemberWorkspaceInfoList(state),
    (workspaces): readonly PlainDropdownItem<BlueprintDropdownSelectData>[] => {
        // Add delete and edit template option
        const dropdownItemPropsList: PlainDropdownItem<BlueprintDropdownSelectData>[] = [
            {
                id: DROPDOWN_ACTIONS.CREATE,
                text: 'Create new video',
                icon: 'add',
                onSelectData: { type: DROPDOWN_ACTIONS.CREATE },
            },
            {
                id: DROPDOWN_ACTIONS.EDIT_BASE_VIDEO,
                text: 'Edit base video',
                icon: 'edit',
                onSelectData: { type: DROPDOWN_ACTIONS.EDIT_BASE_VIDEO },
            },
            {
                id: DROPDOWN_ACTIONS.EDIT_INSTRUCTIONS,
                text: 'Edit instructions',
                icon: 'description',
                onSelectData: { type: DROPDOWN_ACTIONS.EDIT_INSTRUCTIONS },
            },
        ];

        if (SERVER.USER.role === RoleEnum.ADMIN) {
            // add in the "Copy to -> workspace" options, if there are accessible workspaces
            const workspaceOptions: PlainDropdownItem<BlueprintDropdownSelectData>[] =
                workspaces.map((workspace) => ({
                    id: `workspace-${workspace.id}`,
                    text: workspace.name,
                    onSelectData: {
                        type: DROPDOWN_ACTIONS.COPY_TO,
                        workspaceId: workspace.id,
                    },
                }));
            if (workspaceOptions.length > 0) {
                const finalWorkspaceOptions: readonly PlainDropdownItem<BlueprintDropdownSelectData>[] =
                    workspaceOptions.length > 1
                        ? [
                              ...workspaceOptions,
                              {
                                  id: DROPDOWN_DIVIDER,
                              },
                              {
                                  id: 'copy-to-multiple',
                                  text: 'Multiple workspaces...',
                                  onSelectData: {
                                      type: DROPDOWN_ACTIONS.COPY_TO_MULTIPLE,
                                  },
                              },
                          ]
                        : workspaceOptions;

                dropdownItemPropsList.push({
                    id: 'copy-to',
                    nestedDropdown: {
                        toggleProps: {
                            text: 'Copy to',
                            icon: 'content_copy',
                        },
                        limitMenuHeight: true,
                        items: finalWorkspaceOptions,
                    },
                });
            }
        }

        dropdownItemPropsList.push({
            id: DROPDOWN_ACTIONS.DELETE,
            'data-testid': 'delete-blueprint',
            text: 'Delete',
            icon: 'delete',
            onSelectData: { type: DROPDOWN_ACTIONS.DELETE },
        });

        return dropdownItemPropsList;
    },
);

// any video past script composer stage can used as the basis for a blueprint
export const selectBlueprintCandidateVideos = createSelector(
    [(state: RootState) => selectAllVideoIds(state), (state: RootState) => state],
    (videoIds, state) =>
        videoIds.reduce<DeepReadonly<DashboardVideo>[]>((acc, videoId) => {
            const video = selectVideoById(state, videoId);
            if (video && video.is_in_script_composer === false) {
                acc.push(video);
            }
            return acc;
        }, []),
);

export const selectTemplateIdToDisplayNameMap = createSelector(
    [selectBlueprintCandidateVideos, (state: RootState) => state],
    (videos, state) =>
        videos.reduce<Record<string, string>>((acc, video) => {
            const templateId = video.template_id;
            if (!acc[templateId]) {
                const displayName = getTemplateById(state, templateId)?.display_name;
                if (displayName) {
                    acc[templateId] = displayName;
                }
            }
            return acc;
        }, {}),
);
